import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram, Phone } from "lucide-react";
import { api } from "../lib/api";
import { Diamond } from "./Motifs";
import { useLang } from "../lib/i18n";

const QUICK = [
  { to: "/category/businesses", slug: "businesses", label: "Businesses" },
  { to: "/category/restaurants", slug: "restaurants", label: "Restaurants" },
  { to: "/category/jobs", slug: "jobs", label: "Jobs" },
  { to: "/category/housing", slug: "housing", label: "Housing" },
  { to: "/category/marketplace", slug: "marketplace", label: "Marketplace" },
  { to: "/category/services", slug: "services", label: "Services" },
  { to: "/category/events", slug: "events", label: "Events" },
  { to: "/category/community", slug: "community", label: "Community" },
];

export default function Footer({ countries = [] }) {
  const { t, tOr } = useLang();
  const [socials, setSocials] = useState({});
  useEffect(() => {
    api.get("/settings/public").then((r) => setSocials(r.data || {})).catch(() => {});
  }, []);
  const sortedCountries = [...countries].sort(
    (a, b) => (a.status === "active" ? 0 : 1) - (b.status === "active" ? 0 : 1)
  );
  return (
    <footer className="bg-teak text-cream acheik-bg-dark" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-14">
          <div className="md:col-span-5">
            <div className="flex items-center gap-2.5 mb-5">
              <img src="/bh-icon-180.png" alt="BurmeseHub logo" className="w-10 h-10 rounded-full" />
              <span className="font-display font-extrabold tracking-tight text-2xl">
                BURMESE <span className="text-gold">HUB</span>
              </span>
            </div>
            <p className="font-serif italic text-xl text-gold/90 mb-3">{t("footer.tagline")}</p>
            <p className="text-cream/60 text-sm leading-relaxed max-w-sm">
              {t("footer.sub")}
            </p>
            <div className="flex items-center gap-3 mt-6" data-testid="footer-socials">
              {socials.facebook_url && (
                <a href={socials.facebook_url} target="_blank" rel="noopener noreferrer" data-testid="footer-social-facebook"
                  className="w-9 h-9 rounded-full border border-teak-line flex items-center justify-center text-cream/60 hover:text-gold hover:border-gold transition-colors" aria-label="Facebook">
                  <Facebook className="w-4 h-4" />
                </a>
              )}
              {socials.instagram_url && (
                <a href={socials.instagram_url} target="_blank" rel="noopener noreferrer" data-testid="footer-social-instagram"
                  className="w-9 h-9 rounded-full border border-teak-line flex items-center justify-center text-cream/60 hover:text-gold hover:border-gold transition-colors" aria-label="Instagram">
                  <Instagram className="w-4 h-4" />
                </a>
              )}
            </div>
          </div>
          <div className="md:col-span-3">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-gold mb-5">{t("footer.explore")}</p>
            <ul className="grid grid-cols-2 gap-x-4 gap-y-2.5">
              {QUICK.map((q) => (
                <li key={q.to}>
                  <Link to={q.to} data-testid={`footer-link-${q.label.toLowerCase()}`} className="text-sm text-cream/70 hover:text-gold transition-colors">
                    {tOr(`cat.${q.slug}`, q.label)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div className="md:col-span-4">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-gold mb-5">{t("footer.where")}</p>
            <div className="flex flex-wrap gap-2" data-testid="footer-countries">
              {sortedCountries.map((c) => (
                <span
                  key={c.code}
                  data-testid={`footer-country-${c.code}`}
                  className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border ${
                    c.status === "active"
                      ? "border-gold/60 text-gold"
                      : "border-teak-line text-cream/40"
                  }`}
                >
                  <Diamond className={`w-1.5 h-1.5 ${c.status === "active" ? "text-gold" : "text-cream/30"}`} />
                  {tOr(`country.${c.code}`, c.name)}
                  {c.status !== "active" && <span className="text-[10px] uppercase tracking-wider">· {t("footer.soon")}</span>}
                </span>
              ))}
            </div>
            <div className="mt-8 space-y-2">
              <Link to="/advertise" data-testid="footer-advertise-link" className="block text-sm text-cream/70 hover:text-gold transition-colors">
                {t("footer.advertise")}
              </Link>
              <Link to="/submit" data-testid="footer-submit-link" className="block text-sm text-cream/70 hover:text-gold transition-colors">
                {t("footer.submit")}
              </Link>
              <Link to="/owner/login" data-testid="footer-owner-link" className="block text-sm text-cream/70 hover:text-gold transition-colors">
                {t("footer.owners")}
              </Link>
              <Link to="/contact" data-testid="footer-contact-page-link" className="block text-sm text-cream/70 hover:text-gold transition-colors">
                {t("footer.contact")}
              </Link>
              <a href={`mailto:${socials.contact_email || "admin@burmesehub.com"}`} data-testid="footer-contact-link" className="block text-sm text-cream/70 hover:text-gold transition-colors">
                {socials.contact_email || "admin@burmesehub.com"}
              </a>
              {socials.contact_phone && (
                <a href={`tel:${socials.contact_phone}`} data-testid="footer-phone-link" className="flex items-center gap-2 text-sm text-cream/70 hover:text-gold transition-colors">
                  <Phone className="w-3.5 h-3.5" /> {socials.contact_phone}
                </a>
              )}
            </div>
          </div>
        </div>
        <div className="mt-14 pt-7 border-t border-teak-line flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-cream/40">© 2026 BurmeseHub.com — All are welcome here.</p>
          <div className="flex items-center gap-6 text-xs text-cream/40">
            <Link to="/privacy-policy" data-testid="footer-privacy" className="hover:text-gold transition-colors">{t("footer.privacy")}</Link>
            <Link to="/terms-of-service" data-testid="footer-terms" className="hover:text-gold transition-colors">{t("footer.terms")}</Link>
            <Link to="/guidelines" data-testid="footer-community-guidelines" className="hover:text-gold transition-colors">{t("footer.guidelines")}</Link>
            <Link to="/impressum" data-testid="footer-impressum" className="hover:text-gold transition-colors">{t("footer.impressum")}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
